import React, { useState } from "react";
import SortupDialog from "../../components/common/SortupDialog";
import { toast } from "../../@/components/ui/use-toast";
import { formatDateTime } from "../../lib/utils";
import { useBookShipmentByVendorMutation } from "../../api/api";
import { DateTimePicker } from "../GoLivePage/components/DateAndTimePicker";

interface ShipmentService {
  serviceId: string;
  carrierName: string;
  serviceName: string;
  deliveryCharges: number;
  estimatedDeliveryTime: string;
}

interface BookShipmentSectionProps {
  isDialogOpen: boolean;
  setIsDialogOpen: (open: boolean) => void;
  orderId: number;
  getClickShipRates: any;
  isLoading: boolean;
  orderApi: any;
  orderItems: any;
  setIsShippingLabelExpired: (value: boolean) => void;
}

const BookShipmentSection = ({
  isDialogOpen,
  setIsDialogOpen,
  orderId,
  getClickShipRates,
  isLoading,
  orderApi,
  orderItems,
  setIsShippingLabelExpired,
}: BookShipmentSectionProps) => {
  const [selectedService, setSelectedService] =
    useState<ShipmentService | null>(null);
  const [rates, setRates] = useState<ShipmentService[]>([]);
  const [dropoffDate, setDropoffDate] = useState<Date | null>(null);
  const [isFetchingRates, setIsFetchingRates] = useState(false);
  const [bookShipment, { isLoading: bookShipmentLoading }] =
    useBookShipmentByVendorMutation();

  const handleClose = () => {
    setIsDialogOpen(false);
    setDropoffDate(null);
    setRates([]);
    setSelectedService(null);
  };

  const handleFetchRates = async (date: Date) => {
    setDropoffDate(date);
    setIsFetchingRates(true);
    setRates([]);
    setSelectedService(null);

    try {
      const res: any = await getClickShipRates({
        body: {
          orderItemIds: orderItems.map((item: any) => item.id),
          expectedDropoffDateTime: formatDateTime(date, "YYYY-MM-DDTHH:mm:ssZ"),
        },
      });

      if (res?.data?.data?.length) {
        setRates(res.data.data);
      } else {
        toast({
          title: "No Services Found",
          description: "No available shipping services for the selected date.",
          variant: "destructive",
        });
      }
    } catch (err: any) {
      toast({
        title: "Failed to fetch rates",
        description: err?.data?.message || "Try again later",
        variant: "destructive",
      });
    } finally {
      setIsFetchingRates(false);
    }
  };

  const handleBook = async () => {
    if (!selectedService) return;

    try {
      const res: any = await bookShipment({
        body: {
          orderId,
          clickshipServiceId: selectedService.serviceId,
          expectedDropoffDate: formatDateTime(
            dropoffDate,
            "YYYY-MM-DDTHH:mm:ssZ"
          ),
          deliveryCharges: selectedService.deliveryCharges,
        },
      });

      if (res?.data?.data?.message?.includes("successfully")) {
        toast({
          title: "Refresh Labels",
          description: res.data.data.message,
          variant: "success",
        });
        setIsShippingLabelExpired(false);
        orderApi.refetch();
        handleClose();
      } else {
        toast({
          title: "Error refresh labels",
          description: res.error.data.message,
          variant: "destructive",
        });
      }
    } catch (err: any) {
      toast({
        title: "Error refresh labels",
        description:
          err.data?.message || err?.message || "Please try again later.",
        variant: "destructive",
      });
    }
  };

  return (
    <SortupDialog
      isOpen={isDialogOpen}
      onClose={handleClose}
      header="Refresh Labels"
      bodyClassName="w-full mix-w-[37vw]"
      footerButtons={[
        {
          name: bookShipmentLoading ? "Refreshing..." : "Refresh Labels",
          onClick: handleBook,
          className:
            "bg-customGreenColor text-white disabled:opacity-50 px-4 py-2 rounded-md font-medium hover:bg-opacity-90 transition-all",
          disabled: !selectedService || bookShipmentLoading || isLoading,
        },
        {
          name: "Close",
          onClick: handleClose,
          className:
            "bg-white text-gray-900 border border-gray-300 px-4 py-2 rounded-md font-medium hover:bg-gray-50 transition-all",
          disabled: isLoading || bookShipmentLoading,
        },
      ]}
    >
      <div className="flex flex-col gap-6 px-6 bg-white rounded-md">
        <h3 className="text-lg font-semibold text-gray-800 mb-2">
          Choose Dropoff Date and Time
        </h3>
        <DateTimePicker
          value={dropoffDate ? new Date(dropoffDate) : undefined}
          onValueChange={(date: Date | undefined) => {
            date && handleFetchRates(date);
          }}
          isUseForDialog={true}
          isUseForSchedule={true}
        />

        <div className="mt-2 p-3 rounded-md bg-blue-50 text-blue-700">
          <p className="text-sm font-medium flex items-center text-wrap">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5 mr-2"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            Once you choose a delivery option, the delivery cost will be covered
            by the vendor.
          </p>
        </div>

        {isFetchingRates && (
          <div className="flex flex-col gap-4 pt-2">
            {[1, 2, 3].map((item) => (
              <div
                key={item}
                className="animate-pulse flex justify-between items-center p-4 border border-gray-200 rounded-md bg-gray-100"
              >
                <div className="space-y-2">
                  <div className="h-4 w-40 bg-gray-300 rounded" />
                  <div className="h-3 w-32 bg-gray-300 rounded" />
                </div>
                <div className="h-4 w-12 bg-gray-300 rounded" />
              </div>
            ))}
          </div>
        )}

        {rates.length > 0 && (
          <div className="flex flex-col gap-4 pt-2">
            <h4 className="text-md font-semibold text-gray-700">
              Available Shipping Options
            </h4>
            {rates.map((service) => (
              <div
                key={service.serviceId}
                onClick={() => setSelectedService(service)}
                className={`cursor-pointer p-4 border rounded-md ${
                  selectedService?.serviceId === service.serviceId
                    ? "border-customGreenColor bg-green-50"
                    : "border-gray-300"
                } hover:shadow-sm transition-all`}
              >
                <div className="flex justify-between items-center">
                  <div>
                    <p className="font-medium text-gray-800">
                      {service.carrierName} - {service.serviceName}
                    </p>
                    <p className="text-sm text-gray-500">
                      Est. Delivery: {service.estimatedDeliveryTime}
                    </p>
                  </div>
                  <p className="font-semibold text-gray-900">
                    ${service.deliveryCharges.toFixed(2)}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </SortupDialog>
  );
};

export default BookShipmentSection;
